<template>
  <Modal
    :show="show"
    :title="t('messages.auth.session_expiring_title')"
    :hide-close="true"
    :hide-buttons="true"
    size="xs"
  >
    <div class="text-left">
      <p class="text-sm text-secondary -mt-2 mb-8">
        {{
          t('messages.auth.session_expiring_message', { seconds: countdown })
        }}
      </p>
      <Button
        type="primary"
        class="w-full"
        :loading="isRefreshing"
        @click="stayLoggedIn"
      >
        {{ t('messages.auth.stay_logged_in') }}
      </Button>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from '@/components/controls/Button'
import Modal from '@/components/misc/Modal'
import TokenService from '@/services/user/token'
import UserService from '@/services/user/user'

const props = defineProps<{ show: boolean }>()
const emit = defineEmits<{ close: []; expired: [] }>()

const { t } = useI18n()
const countdown = ref(0)
const isRefreshing = ref(false)
let countdownInterval: ReturnType<typeof setInterval> | null = null

function startCountdown() {
  const remaining = TokenService.getTimeUntilExpiry()
  countdown.value = remaining !== null ? Math.max(0, remaining) : 0

  countdownInterval = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      stopCountdown()
      emit('expired')
    }
  }, 1000)
}

function stopCountdown() {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
}

async function stayLoggedIn() {
  isRefreshing.value = true
  try {
    await UserService.getMe()
    stopCountdown()
    emit('close')
  } catch {
    stopCountdown()
    emit('expired')
  } finally {
    isRefreshing.value = false
  }
}

watch(
  () => props.show,
  (visible) => {
    if (visible) {
      startCountdown()
    } else {
      stopCountdown()
    }
  }
)

onBeforeUnmount(() => {
  stopCountdown()
})
</script>
